import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from './Toast';

const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const { success } = useToast();

    // 페이지 로드시 저장된 사용자 정보 확인
    useEffect(() => {
        const checkAuthStatus = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');
            const savedDarkMode = localStorage.getItem('darkMode');

            // 저장된 다크모드 설정을 즉시 적용
            if (savedDarkMode === 'true') {
                setIsDarkMode(true);
                document.body.classList.add('dark-mode');
            } else {
                setIsDarkMode(false);
                document.body.classList.remove('dark-mode');
            }

            if (token && savedUser) {
                try {
                    // 서버에서 사용자 정보 재확인
                    const response = await fetch('/api/auth/me', {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (response.ok) {
                        const userData = await response.json();
                        setUser(userData);
                        // 서버의 다크모드 설정으로 로컬 스토리지 업데이트
                        const serverDarkMode = Boolean(userData.dark_mode);
                        setIsDarkMode(serverDarkMode);
                        localStorage.setItem('darkMode', serverDarkMode.toString());
                        document.body.classList.toggle('dark-mode', serverDarkMode);
                    } else {
                        // 토큰이 유효하지 않으면 로컬 스토리지 정리
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        localStorage.removeItem('darkMode');
                    }
                } catch (error) {
                    console.error('Auth check failed:', error);
                    // 네트워크 오류 시 로컬 데이터 사용
                    try {
                        const userData = JSON.parse(savedUser);
                        setUser(userData);
                        // 저장된 다크모드 설정 유지 (이미 위에서 적용됨)
                    } catch {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        localStorage.removeItem('darkMode');
                    }
                }
            }
            setIsLoading(false);
        };

        checkAuthStatus();
    }, []);

    const login = (userData, token, adminToken = null) => {
        setUser(userData);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // 관리자 토큰이 있으면 저장
        if (adminToken) {
            localStorage.setItem('adminToken', adminToken);
        }
        
        // 다크모드 설정 적용 및 저장
        const darkModeEnabled = Boolean(userData.dark_mode);
        setIsDarkMode(darkModeEnabled);
        localStorage.setItem('darkMode', darkModeEnabled.toString());
        document.body.classList.toggle('dark-mode', darkModeEnabled);
    };

    const logout = async () => {
        const token = localStorage.getItem('token');
        
        if (token) {
            try {
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            } catch (error) {
                console.error('Logout error:', error);
            }
        }

        setUser(null);
        setIsDarkMode(false);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('darkMode');
        localStorage.removeItem('adminToken');  // 관리자 토큰도 삭제
        
        // 다크모드 해제
        document.body.classList.remove('dark-mode');
        
        // 로그아웃 성공 메시지
        success('로그아웃되었습니다.');
    };

    const updateUser = (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // 다크모드 설정 적용 및 저장
        const darkModeEnabled = Boolean(userData.dark_mode || userData.darkMode);
        setIsDarkMode(darkModeEnabled);
        localStorage.setItem('darkMode', darkModeEnabled.toString());
        document.body.classList.toggle('dark-mode', darkModeEnabled);
    };

    const updateDarkMode = async (darkModeEnabled) => {
        // 로컬 스토리지와 UI 즉시 업데이트
        setIsDarkMode(darkModeEnabled);
        localStorage.setItem('darkMode', darkModeEnabled.toString());
        document.body.classList.toggle('dark-mode', darkModeEnabled);

        // 로그인된 사용자의 경우 서버에도 업데이트
        if (isAuthenticated()) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch('/api/auth/update-dark-mode', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ dark_mode: darkModeEnabled })
                });

                if (response.ok) {
                    const updatedUser = await response.json();
                    // 사용자 정보 업데이트
                    setUser(prevUser => ({
                        ...prevUser,
                        ...updatedUser,
                        dark_mode: darkModeEnabled
                    }));
                    localStorage.setItem('user', JSON.stringify({
                        ...user,
                        ...updatedUser,
                        dark_mode: darkModeEnabled
                    }));
                } else {
                    console.error('Failed to update dark mode on server');
                }
            } catch (error) {
                console.error('Error updating dark mode:', error);
            }
        }
    };

    const isAuthenticated = () => {
        return !!user && !!localStorage.getItem('token');
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        const adminToken = localStorage.getItem('adminToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // 관리자 토큰이 있으면 헤더에 추가
        if (adminToken) {
            headers['X-Admin-Token'] = adminToken;
        }
        
        return headers;
    };

    // 관리자 여부 확인 (adminToken 존재 여부로 판단)
    const isAdmin = () => {
        return !!localStorage.getItem('adminToken');
    };

    // 사용자 정보 재확인 (관리자 권한 포함)
    const refreshUserInfo = async () => {
        const token = localStorage.getItem('token');
        if (!token) return false;

        try {
            const response = await fetch('/api/auth/me', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
                
                // 다크모드 설정 동기화
                const darkModeEnabled = Boolean(userData.dark_mode);
                setIsDarkMode(darkModeEnabled);
                localStorage.setItem('darkMode', darkModeEnabled.toString());
                document.body.classList.toggle('dark-mode', darkModeEnabled);
                
                return true;
            } else {
                // 토큰이 유효하지 않으면 로그아웃
                logout();
                return false;
            }
        } catch (error) {
            console.error('사용자 정보 갱신 실패:', error);
            return false;
        }
    };

    const value = {
        user,
        isLoading,
        isDarkMode,
        login,
        logout,
        updateUser,
        updateDarkMode,
        isAuthenticated,
        getAuthHeaders,
        refreshUserInfo,
        isAdmin
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
